import { Badge, Box, Button, Flex, HStack, StackDivider, Text } from '@chakra-ui/react'
import { PopUpMessageContext, useApp, useCachedUser } from '@hb/react'
import {
  capitalizeFirstLetter,
  Encounter,
  EncounterStatus,
  getDateTimeString,
  getFullName,
} from '@hb/shared'
import { useCallback, useContext, useState } from 'react'
import { placeEncounterBillingHold } from '../../../backend/functions/gyn'
import { BillingHoldReasonModal } from '../../BillingHoldReasonModal'
import { getEncounterHoldAction } from './billingHold'
import { getEncounterTimestampDisplay } from './encounterTimestamps'

const ProviderDisplay = ({
  type,
  providerId,
  initials,
}: {
  type: 'rendering' | 'supervising' | 'attending'
  providerId: string | null
  initials: string | null
}) => {
  const { data: provider } = useCachedUser(providerId)
  const name = provider ? getFullName(provider) : providerId ? '...' : '—'
  return (
    <Box w="100%">
      <Text
        fontSize="xs"
        color="gray.500"
        fontWeight={600}
        textTransform="uppercase"
        letterSpacing="0.04em">
        {capitalizeFirstLetter(type)} Provider
      </Text>
      <Flex align="center" gap={2} mt={0.5}>
        <Text fontSize="sm" fontWeight={500}>
          {name}
        </Text>
        {initials ? (
          <Text fontSize="xs" color="gray.500">
            ({initials})
          </Text>
        ) : null}
      </Flex>
    </Box>
  )
}

// uid → display name, resolved the same way for both Created and Last Modified identities.
const UserName = ({ uid }: { uid: string }) => {
  const { data: user } = useCachedUser(uid)
  return <>{user ? getFullName(user) : uid ? '…' : '—'}</>
}

// §1.6.1 dual timestamp, note level. Muted metadata row; renders in both apps (part of the
// note's record, no billing language). Collapses to "Created …" when never edited; shows both
// identities only when the last editor differs from the creator. Per-section/per-entry
// granularity is out of scope (decision 6 — unresolved).
const NoteTimestamps = ({ encounter }: { encounter: Encounter }) => {
  const display = getEncounterTimestampDisplay(encounter)
  return (
    <Flex
      align="center"
      gap={1}
      flexWrap="wrap"
      pt={0.5}
      borderTop="1px solid #e2e8f0"
      fontSize="xs"
      color="gray.500"
      _dark={{ color: 'gray.400' }}>
      <Text as="span">
        Created {getDateTimeString(display.createdOn, 'short')} by{' '}
        <UserName uid={display.createdBy} />
      </Text>
      {display.lastModified ? (
        <Text as="span">
          {' · '}Last modified {getDateTimeString(display.lastModified.updatedOn, 'short')}
          {display.differentEditor ? (
            <>
              {' '}
              by <UserName uid={display.lastModified.updatedBy} />
            </>
          ) : null}
        </Text>
      ) : null}
    </Flex>
  )
}

const FaceToFaceDisplay = ({ minutes }: { minutes?: number | null }) => (
  <Box w="100%">
    <Text
      fontSize="xs"
      color="gray.500"
      fontWeight={600}
      textTransform="uppercase"
      letterSpacing="0.04em">
      Visit Time
    </Text>
    <Text fontSize="sm" fontWeight={500} mt={0.5}>
      {minutes != null ? `${minutes} min` : '—'}
    </Text>
  </Box>
)

export const EncounterViewFooter = ({
  encounter,
  encounterId,
}: {
  encounter: Encounter
  encounterId: string
}) => {
  const auth = encounter.authentication
  const amendCount = auth?.previousVersions?.length ?? 0
  const { appName } = useApp()
  const { processResponse } = useContext(PopUpMessageContext)
  const holdAction = getEncounterHoldAction(encounter, appName)
  const [holdModalOpen, setHoldModalOpen] = useState(false)

  const handlePlaceHold = useCallback(
    async (reason: string) => {
      await placeEncounterBillingHold({ encounterId, appName, action: 'place', reason })
      processResponse({ success: 'Billing hold placed' })
    },
    [encounterId, appName, processResponse],
  )

  const handleReleaseHold = useCallback(
    async (reason: string) => {
      await placeEncounterBillingHold({ encounterId, appName, action: 'release', reason })
      processResponse({ success: 'Billing hold released' })
    },
    [encounterId, appName, processResponse],
  )

  return (
    <Flex
      flexFlow="column"
      gap={2}
      borderTop="1px solid #cdcdcd"
      bg="gray.50"
      _dark={{ bg: 'gray.800' }}
      px={4}
      pt={2}
      pb={3}
      w="100%"
      flexShrink={0}>
      <HStack w="100%" spacing={4} divider={<StackDivider />} flexWrap="wrap">
        <Box flex={1} minW="120px">
          <ProviderDisplay
            type="rendering"
            providerId={encounter.renderingProvider}
            initials={encounter.renderingProviderInitials}
          />
        </Box>
        <Box flex={1} minW="120px">
          <ProviderDisplay
            type="supervising"
            providerId={encounter.supervisingProvider}
            initials={encounter.supervisingProviderInitials}
          />
        </Box>
        <Box flex={1} minW="120px">
          <ProviderDisplay
            type="attending"
            providerId={encounter.attendingProvider}
            initials={encounter.attendingProviderInitials}
          />
        </Box>
        <Box flex={1} minW="80px">
          <FaceToFaceDisplay minutes={encounter.data?.elapsedTime?.faceToFaceMinutes} />
        </Box>
      </HStack>

      {/* §2.11: Authentication timestamp — displayed to the second when encounter is authenticated. */}
      {encounter.status === EncounterStatus.AUTHENTICATED && auth ? (
        <Flex align="center" gap={2} flexWrap="wrap" pt={0.5} borderTop="1px solid #e2e8f0">
          <Badge colorScheme="blue" fontSize="2xs" px={2} flexShrink={0}>
            Authenticated
          </Badge>
          <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }}>
            {auth.providerFirst} {auth.providerLast}
          </Text>
          <Text fontSize="xs" color="gray.400">
            {/* §1.6: authentication is time-stamped to the second. */}
            {getDateTimeString(auth.authenticatedOn, 'long', false, true)}
          </Text>
          {/* Summary only — the record itself is the Amendment History section in the note body. */}
          {amendCount > 0 && (
            <Text fontSize="xs" color="orange.500" fontWeight={500}>
              (amended {amendCount} {amendCount === 1 ? 'time' : 'times'} — see Amendment History)
            </Text>
          )}
        </Flex>
      ) : null}

      {/* §1.6.1 Created / Last Modified — note-level, both apps. */}
      <NoteTimestamps encounter={encounter} />

      {/* §1.6.2 Hold from Billing — muted gray link, providers app + authenticated notes only.
          Spec-sanctioned exception to the no-billing-language rule; do not reword. */}
      {holdAction ? (
        <Button
          variant="link"
          size="xs"
          color="gray.400"
          fontWeight={400}
          alignSelf="flex-start"
          onClick={() => setHoldModalOpen(true)}>
          {holdAction === 'place' ? 'Hold from billing' : 'Release hold'}
        </Button>
      ) : null}
      {holdModalOpen && holdAction === 'place' ? (
        <BillingHoldReasonModal
          isOpen
          onClose={() => setHoldModalOpen(false)}
          header="Hold from billing"
          reasonLabel="Reason for hold (required)"
          reasonPlaceholder="e.g. Patient self-pay — invoice directly"
          reasonRequired
          confirmLabel="Confirm"
          errorMessage="Failed to place billing hold"
          onSubmit={handlePlaceHold}
        />
      ) : null}
      {holdModalOpen && holdAction === 'release' ? (
        <BillingHoldReasonModal
          isOpen
          onClose={() => setHoldModalOpen(false)}
          header="Release hold"
          reasonLabel="Why are you releasing this hold?"
          reasonPlaceholder="Optional"
          reasonRequired={false}
          confirmLabel="Confirm"
          errorMessage="Failed to release billing hold"
          onSubmit={handleReleaseHold}
        />
      ) : null}
    </Flex>
  )
}
